// Prechecks for farm recalculations that DON'T run the offer form's Yup schema.
//
// Several paths re-rate an offer by spreading the persisted quote — the pre-bind farm UW page and the
// resubmit-offer modal each submit their own small form. The offer-page rules never run there, so a
// precondition that lives only in Yup is silently bypassed and the bad payload reaches ClarionDoor,
// failing with a rater error instead of a fixable form message. Each helper below is one of those
// preconditions, expressed so it can be reused as a backstop on those paths.
import { hasInlandMarineSppItems, isInlandMarineRiskType } from '@ourbranch/farm-utils';

// Issue #31351 follow-up: the offer-page Yup rule for
// `farmLiabilityCoverage.farmOperations` (PR #31354) only fires on the offer
// form. The pre-bind farm UW page submits its own form and recalculates by
// spreading the persisted quote, so legacy offers saved with empty
// `farmOperations` reach ClarionDoor and 5042 there. This helper is the same
// precondition, reused as a backstop on any non-offer-form recalc.
export const isMissingPrimaryFarmOperations = (quote) =>
  Boolean(quote?.includeFarmLiability && !quote?.farmLiabilityCoverage?.farmOperations);

/**
 * Whether the quote holds a standalone inland marine risk with no SPP items (#33164 —
 * docs/farm-standalone-inland-marine.md).
 *
 * The risk's SPP items are its entire premium, and a farm offer's total premium can never be $0 — so an
 * empty inland marine risk fails outright at ClarionDoor with "did not receive premium". A populated
 * sibling risk does not save it: CFM rates one ClarionDoor request per risk, so the empty one totals
 * $0 on its own request, and any errored rate fails the whole all-or-nothing farm rate. The BE's
 * FARM_INLAND_MARINE_NO_ITEMS soft rejection flags the empty risk but cannot make it rate. staff-fe
 * blocks this on the
 * offer form (`required-inland-marine-item` in farm-shared.validation-schema) and collects the first
 * item at new-offer time, so an empty risk normally cannot exist. It stays reachable for an offer
 * created outside staff-fe — the BE accepts zero items and flags rather than refusing — and that is
 * precisely the case these schema-less recalc paths would otherwise push straight into a rater error.
 */
export const hasInlandMarineRiskWithoutItems = (quote) =>
  (quote?.farmLocations || []).some(
    (location) =>
      location &&
      !location.isDeleted &&
      (location.risks || []).some(
        (risk) => risk && !risk.isDeleted && isInlandMarineRiskType(risk.type) && !hasInlandMarineSppItems(risk)
      )
  );
